import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Sparkles, Loader2, ExternalLink, Check } from 'lucide-react';
import { perplexityApi, resolveAssetUrl } from '../services/api';
import { toast } from 'sonner';

const LABELS = {
  razon_social: 'Razón social',
  cif: 'CIF',
  web: 'Web',
  telefono: 'Teléfono',
  email: 'Email',
  direccion: 'Dirección',
  codigo_postal: 'Código postal',
  localidad: 'Localidad',
  provincia: 'Provincia',
  pais: 'País',
  cnae: 'CNAE',
  sector: 'Sector',
  año_fundacion: 'Año fundación',
  empleados_aprox: 'Empleados aprox.',
  facturacion: 'Facturación',
  logo_url: 'Logo',
  linkedin_url: 'LinkedIn',
  tipo: 'Tipo',
  naturaleza_juridica: 'Naturaleza jurídica',
  ambito_distribuidor: 'Ámbito distribuidor',
  grupo_empresarial: 'Grupo empresarial',
};

/**
 * Modal para buscar una empresa en Perplexity y aplicar campos selectivamente.
 * Props:
 *  - open / onOpenChange
 *  - entidad: la entidad actual (para valores comparativos)
 *  - onApply(patch): se llama con los campos elegidos cuando el usuario confirma
 */
export const PerplexityBuscarModal = ({ open, onOpenChange, entidad, onApply }) => {
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [seleccion, setSeleccion] = useState({}); // {campo: bool}

  const buscar = async () => {
    setCargando(true); setResultado(null);
    try {
      const data = await perplexityApi.buscarEmpresa({
        cif: entidad?.cif || null,
        razon_social: entidad?.razon_social || null,
      });
      setResultado(data);
      // Pre-selección por defecto: TODOS los campos que Perplexity haya devuelto.
      // El usuario podrá filtrar con los botones "Sólo vacíos / Todos".
      const ini = {};
      Object.keys(LABELS).forEach((k) => {
        const nuevo = data?.[k];
        if (nuevo !== null && nuevo !== undefined && nuevo !== '') ini[k] = true;
      });
      setSeleccion(ini);
    } catch (e) {
      toast.error(e.message || 'Perplexity no respondió');
    } finally { setCargando(false); }
  };

  const marcarTodos = () => {
    const sel = {};
    Object.keys(LABELS).forEach((k) => {
      const nuevo = resultado?.[k];
      if (nuevo !== null && nuevo !== undefined && nuevo !== '') sel[k] = true;
    });
    setSeleccion(sel);
  };

  const marcarSoloVacios = () => {
    const sel = {};
    Object.keys(LABELS).forEach((k) => {
      const actual = entidad?.[k];
      const nuevo = resultado?.[k];
      if (nuevo !== null && nuevo !== undefined && nuevo !== '' && (!actual || actual === '')) sel[k] = true;
    });
    setSeleccion(sel);
  };

  const desmarcarTodos = () => setSeleccion({});

  const aplicar = async () => {
    const patch = {};
    Object.entries(seleccion).forEach(([k, v]) => {
      if (v && resultado?.[k] != null) patch[k] = resultado[k];
    });
    if (Object.keys(patch).length === 0) {
      toast.info('No has seleccionado ningún campo para aplicar');
      return;
    }
    try {
      await onApply(patch);
      onOpenChange(false);
      setResultado(null); setSeleccion({});
    } catch {
      // onApply already shows toast.error — keep modal open so user can retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-pplx-empresa">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" /> Buscar con Perplexity
          </DialogTitle>
          <DialogDescription>
            Busca información pública verificada de <strong>{entidad?.razon_social}</strong> ({entidad?.cif || 'sin CIF'}) y aplica los campos que quieras.
          </DialogDescription>
        </DialogHeader>

        {!resultado && (
          <div className="py-6 text-center">
            <Button
              onClick={buscar}
              disabled={cargando}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="btn-pplx-buscar"
            >
              {cargando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {cargando ? 'Buscando con Perplexity…' : 'Buscar ahora'}
            </Button>
          </div>
        )}

        {resultado && (
          <div className="py-2">
            {resultado._raw && (
              <div className="p-3 mb-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                ⚠ Perplexity devolvió respuesta no parseable. Texto plano: <em>{resultado._raw}</em>
              </div>
            )}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs text-ink-500">Pre-selección:</span>
              <button type="button" onClick={marcarTodos} className="text-xs px-2 py-1 rounded bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100" data-testid="pplx-marcar-todos">Todos los campos</button>
              <button type="button" onClick={marcarSoloVacios} className="text-xs px-2 py-1 rounded bg-ink-50 border border-ink-200 text-ink-700 hover:bg-ink-100" data-testid="pplx-marcar-vacios">Solo campos vacíos</button>
              <button type="button" onClick={desmarcarTodos} className="text-xs px-2 py-1 rounded bg-white border border-ink-200 text-ink-600 hover:bg-ink-50" data-testid="pplx-marcar-ninguno">Ninguno</button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-ink-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-1.5 text-left w-4"></th>
                  <th className="px-2 py-1.5 text-left">Campo</th>
                  <th className="px-2 py-1.5 text-left">Valor actual</th>
                  <th className="px-2 py-1.5 text-left">Perplexity ✦</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {Object.keys(LABELS).map((campo) => {
                  const nuevo = resultado[campo];
                  if (nuevo == null || nuevo === '') return null;
                  const actual = entidad?.[campo];
                  const conflicto = actual && String(actual).trim() !== String(nuevo).trim();
                  return (
                    <tr key={campo} className={conflicto ? 'bg-amber-50/40' : ''}>
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={!!seleccion[campo]}
                          onChange={(e) => setSeleccion({ ...seleccion, [campo]: e.target.checked })}
                          className="accent-violet-600"
                          data-testid={`pplx-chk-${campo}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 font-medium text-ink-700">{LABELS[campo]}</td>
                      <td className="px-2 py-1.5 text-ink-500 truncate max-w-xs">{actual || <em className="text-ink-300">vacío</em>}</td>
                      <td className="px-2 py-1.5 text-ink-900 truncate max-w-xs">
                        {campo === 'logo_url' ? <img src={resolveAssetUrl(nuevo)} alt="" className="h-6" /> : String(nuevo)}
                        {conflicto && <span className="ml-1 text-[10px] text-amber-700">⚠ conflicto</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {resultado.fuentes?.length > 0 && (
              <div className="mt-3 text-xs text-ink-500">
                Fuentes:{' '}
                {resultado.fuentes.slice(0, 5).map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline mr-2">
                    <ExternalLink className="w-3 h-3 inline" /> {i + 1}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          {resultado && (
            <Button onClick={aplicar} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="btn-pplx-aplicar">
              <Check className="w-4 h-4 mr-1.5" /> Aplicar campos seleccionados ({Object.values(seleccion).filter(Boolean).length})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PerplexityBuscarModal;
